import React, { useState } from "react";
import {
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Tooltip,
  Typography,
  makeStyles,
} from "@material-ui/core";
import VisibilityIcon from "@material-ui/icons/Visibility";
import { useSnackbar } from "notistack";
import { clientBackendPDF } from "configurations/axios";
import ContableTable from "../components/ContableTable";
import SelectorMesAnio from "../components/SelectorMesAnio";
import usePagosCobros from "../hooks/useGastos";

const useStyles = makeStyles((theme) => ({
  root: {
    minHeight: "calc(100vh - 96px)",
    background: "#080f1f",
    padding: theme.spacing(3),
  },
  header: {
    alignItems: "center",
    display: "flex",
    justifyContent: "space-between",
    marginBottom: theme.spacing(2.5),
  },
  title: {
    color: "#f5f7fb",
    fontSize: 19,
    fontWeight: 900,
  },
  number: {
    color: "#bcd8f8",
    fontWeight: 900,
  },
  emisor: {
    color: "#acd0f6",
    fontSize: 13,
  },
  badge: {
    background: "#1a2e4a",
    borderRadius: 12,
    color: "#8bbcff",
    display: "inline-block",
    fontSize: 11,
    fontWeight: 900,
    padding: "2px 10px",
  },
  badgePagado: {
    background: "#073f3f",
    color: "#02dfb1",
  },
  badgePendiente: {
    background: "#3a2807",
    color: "#ffb229",
  },
  viewButton: {
    color: "#8bbcff",
    minWidth: 0,
    padding: theme.spacing(0.5),
  },
  /* ── Stat cards ── */
  cards: {
    display: "flex",
    gap: theme.spacing(2),
    marginBottom: theme.spacing(3),
  },
  card: {
    background: "#111827",
    border: "1px solid #1d3454",
    borderRadius: 10,
    flex: "1 1 0",
    padding: theme.spacing(2, 2.5),
  },
  cardLabel: {
    color: "#53647e",
    fontSize: 11,
    fontWeight: 900,
    letterSpacing: 1.2,
    marginBottom: theme.spacing(1),
    textTransform: "uppercase",
  },
  cardValue: {
    color: "#02dfb1",
    fontSize: 28,
    fontWeight: 900,
    lineHeight: 1,
  },
  cardSub: {
    color: "#8fb1d8",
    fontSize: 12,
    marginTop: theme.spacing(0.5),
  },
  /* ── Dialog PDF ── */
  dialogPaper: {
    background: "#080f1f",
    color: "#f5f7fb",
  },
  dialogTitle: {
    background: "#0d172b",
    borderBottom: "1px solid #1d3454",
    padding: theme.spacing(2, 3),
  },
  dialogTitleText: {
    color: "#f5f7fb",
    fontSize: 17,
    fontWeight: 900,
  },
  dialogSubtitle: {
    color: "#8fb1d8",
    fontSize: 12,
    marginTop: theme.spacing(0.5),
  },
  dialogContent: {
    minHeight: "76vh",
    padding: theme.spacing(2),
  },
  ridePanel: {
    background: "#111a2b",
    border: "1px solid #1d3454",
    borderRadius: 8,
    height: "76vh",
    overflow: "hidden",
    position: "relative",
  },
  rideFrame: {
    background: "#ffffff",
    border: 0,
    height: "100%",
    width: "100%",
  },
  rideLoader: {
    alignItems: "center",
    color: "#acd0f6",
    display: "flex",
    gap: theme.spacing(1),
    height: "100%",
    justifyContent: "center",
  },
  dialogActions: {
    background: "#0d172b",
    borderTop: "1px solid #1d3454",
    padding: theme.spacing(1.5, 3),
  },
  closeButton: {
    color: "#acd0f6",
    textTransform: "none",
  },
}));

/* ── Helpers ── */
const formatDate = (date) => {
  if (!date) return "";
  return new Date(date).toLocaleDateString("es-EC", {
    day: "2-digit",
    month: "short",
    year: "numeric",
  });
};

const formatCurrency = (value) => {
  if (value === undefined || value === null || value === "") return "—";
  return `$${Number(value).toLocaleString("en-US", {
    minimumFractionDigits: 2,
    maximumFractionDigits: 2,
  })}`;
};

const getVoucherNumber = (voucher) => {
  const c = voucher.comprobanteElectronico || {};
  return [c.codigoSucursal, c.puntoFacturacion, c.numeroFactura]
    .filter(Boolean)
    .join("-");
};

/* ── Definición de columnas ─────────────────────────────────────────────────
   Agrega, quita o reordena entradas para personalizar la tabla.
   Cada columna: { key, label, render(row) => node, align? }           */
const buildColumns = (classes, openRide) => [
  {
    key: "numero",
    label: "Número",
    render: (row) => (
      <span className={classes.number}>{getVoucherNumber(row)}</span>
    ),
  },
  {
    key: "fecha",
    label: "Fecha emisión",
    render: (row) => formatDate(row.comprobanteElectronico?.fechaEmision),
  },
  {
    key: "emisor",
    label: "Emisor",
    render: (row) => (
      <span className={classes.emisor}>
        {row.comprobanteElectronico?.razonSocialEmisor || "—"}
      </span>
    ),
  },
  {
    key: "rucEmisor",
    label: "RUC Emisor",
    render: (row) => row.comprobanteElectronico?.rucEmisor || "—",
  },
  {
    key: "tipo",
    label: "Tipo",
    render: (row) =>
      row.comprobanteElectronico?.tipoComprobanteElectronico
        ?.nombreComprobante || "—",
  },
  {
    key: "subtotal",
    label: "Subtotal",
    align: "right",
    render: (row) => formatCurrency(row.comprobanteElectronico?.valorSubtotal),
  },
  {
    key: "iva",
    label: "ITBMS",
    align: "right",
    render: (row) => formatCurrency(row.comprobanteElectronico?.valorItbms),
  },
  {
    key: "total",
    label: "Total",
    align: "right",
    render: (row) => formatCurrency(row.comprobanteElectronico?.valorTotal),
  },
  {
    key: "acciones",
    label: "Acciones",
    render: (row) => (
      <Tooltip title="Ver comprobante (PDF)">
        <Button className={classes.viewButton} onClick={() => openRide(row)}>
          <VisibilityIcon fontSize="small" />
        </Button>
      </Tooltip>
    ),
  },
];
/* ─────────────────────────────────────────────────────────────────────────── */

const GastosView = () => {
  const classes = useStyles();
  const { enqueueSnackbar } = useSnackbar();

  const {
    receivedVouchers,
    page,
    rowsPerPage,
    onPageChange,
    onRowsPerPageChange,
    mes,
    anio,
    onMesChange,
    onAnioChange,
  } = usePagosCobros();

  const [selectedVoucher, setSelectedVoucher] = useState(null);
  const [pdfOpen, setPdfOpen] = useState(false);
  const [rideUrl, setRideUrl] = useState("");
  const [rideLoading, setRideLoading] = useState(false);

  const openRide = async (voucher) => {
    const idComprobante = voucher.comprobanteElectronico?.idComprobanteElectronico;

    setSelectedVoucher(voucher);
    setPdfOpen(true);
    setRideLoading(true);

    if (rideUrl) {
      window.URL.revokeObjectURL(rideUrl);
      setRideUrl("");
    }

    try {
      const response = await clientBackendPDF.get(
        `/utilidades/generar/ride/${idComprobante}`,
      );
      const file = new Blob([response.data], { type: "application/pdf" });
      setRideUrl(window.URL.createObjectURL(file));
    } catch {
      enqueueSnackbar("No se pudo cargar el RIDE del comprobante.", {
        variant: "error",
      });
    } finally {
      setRideLoading(false);
    }
  };

  const closeRide = () => {
    setPdfOpen(false);
    setSelectedVoucher(null);
    if (rideUrl) {
      window.URL.revokeObjectURL(rideUrl);
      setRideUrl("");
    }
  };

  const columns = buildColumns(classes, openRide);

  return (
    <div className={classes.root}>
      <SelectorMesAnio
        mes={mes}
        anio={anio}
        onMesChange={onMesChange}
        onAnioChange={onAnioChange}
      />

      <ContableTable
        columns={columns}
        rows={receivedVouchers.comprobantes}
        totalElements={receivedVouchers.totalElements}
        page={page}
        rowsPerPage={rowsPerPage}
        onPageChange={onPageChange}
        onRowsPerPageChange={onRowsPerPageChange}
        loading={receivedVouchers.loading}
        error={receivedVouchers.error}
        emptyMessage="No existen comprobantes recibidos para mostrar."
        loadingMessage="Cargando comprobantes recibidos..."
      />

      {/* ── Visor PDF ── */}
      <Dialog
        classes={{ paper: classes.dialogPaper }}
        fullWidth
        maxWidth="lg"
        onClose={closeRide}
        open={pdfOpen}
      >
        <DialogTitle className={classes.dialogTitle} disableTypography>
          <Typography className={classes.dialogTitleText}>
            {selectedVoucher?.comprobanteElectronico?.tipoComprobanteElectronico
              ?.nombreComprobante || "Comprobante"}{" "}
            {selectedVoucher ? getVoucherNumber(selectedVoucher) : ""}
          </Typography>
          <Typography className={classes.dialogSubtitle}>
            {selectedVoucher?.comprobanteElectronico?.razonSocialEmisor || ""}
          </Typography>
        </DialogTitle>

        <DialogContent className={classes.dialogContent}>
          <div className={classes.ridePanel}>
            {rideLoading && (
              <div className={classes.rideLoader}>
                <CircularProgress color="inherit" size={24} />
                <span>Cargando RIDE...</span>
              </div>
            )}
            {!rideLoading && rideUrl && (
              <iframe
                className={classes.rideFrame}
                src={rideUrl}
                title="RIDE comprobante recibido"
              />
            )}
            {!rideLoading && !rideUrl && (
              <div className={classes.rideLoader}>
                No se pudo mostrar el RIDE.
              </div>
            )}
          </div>
        </DialogContent>

        <DialogActions className={classes.dialogActions}>
          <Button className={classes.closeButton} onClick={closeRide}>
            Cerrar
          </Button>
        </DialogActions>
      </Dialog>
    </div>
  );
};

export default GastosView;
